import { Typography } from "@mui/material";
import AutocompleteField from "controls/global/custom-autocomplete/AutocompleteField";
import ConfirmationDialog from "controls/global/dialogs/confirmation-dialog";
import { SCFileResponse } from "entities/UIModel";
import { isSCFileResponse } from "pages/file/utils/autocomplete";
import React,
{
  ChangeEvent,
  useEffect,
  useRef,
} from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { useDuplicatedFileCheck } from "utils/context/DuplicatedFileCheckContext";
import { useFileAutocomplete } from "utils/context/FileAutocompleteContext";
import { useFiles } from "utils/context/FilesContext";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { AgencyStatus, PERMISSIONS } from "utils/data/enum";
import {
  REGEX_NONE_ASCII,
} from "utils/shared";

const FileNameNumberFieldName = "fileNameNumber";
const FileNameNumberField = ({ ...rest }) => {
  const fileNameValueRef = useRef();
  const agencyIdValueRef = useRef();
  // Name of the file the user just picked from the dropdown, while we navigate to it.
  const selectedExistingFileRef = useRef<string | undefined>();

  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = (profileSettings?.userPermissionItemTypes as string[]);
  const hasCancelledAgencyPermission = permissionCodes?.includes(PERMISSIONS.AGENCY_CANCEL_FILE_ACCESS);

  const navigate = useNavigate();
  const [{ selectedAgency, }, { setFileNumber }] = useGlobalAccess();  
  
  const { setValue, getValues, setError, clearErrors } = useFormWrapper();
  const watchFields = useWatch({ name: ["id", "agency", "fileNameNumber"] });
  const idField = watchFields[0];
  const agencyField = watchFields[1];
  const fileNameNumber = watchFields[2];

  const [, { resetNonFileSections, resetInitialValues }] = useFiles();
  const [, { getFiles }] = useFileAutocomplete();  
  const [
    {
      isDuplicatedFile,
      showAlertDuplicatedFile,
      prevFileNameValueState,
      uniqueFileId,
      integrationKey,
      isToggleFileAgencyFocus,
    },
    {
      getUniqueFileByAgencyClientFileId,
      setIsDuplicatedFile,
      setShowAlertDuplicatedFile,
      setToggleFileAgencyFocus,
      setIsOpeningExistingFile,
    },
  ] = useDuplicatedFileCheck();

  const getAgencySelected = () => {
    if(selectedAgency?.length > 0){
      return selectedAgency[0].CompanyID;
    }    
  };

  const isAgencyIssueable = () => {
    if(selectedAgency?.length > 0){
      const isIssuable = selectedAgency[0].Status?.toUpperCase() === AgencyStatus.Active 
        && (  
          !selectedAgency[0].IsCancelled 
          || (
            selectedAgency[0].IsCancelled 
            && hasCancelledAgencyPermission
          )
        )
      // return selectedAgency[0].Status?.toUpperCase() === AgencyStatus.Active;
      return isIssuable;
    }    
    return true;
  };

  useEffect(() => {
    checkValidationFileName();
    // if(isDuplicatedFile) {
    //   clearErrors(FileNameNumberFieldName);
    // }
  }, [isDuplicatedFile]);

  useEffect(() => {
    fileNameValueRef.current = undefined;
  }, [prevFileNameValueState]);

  // The file we were opening has landed in the form. Drop the suppression (so it
  // can't hide a legitimate warning for the next file the user creates) and close
  // any alert that was raised while the file was loading.
  useEffect(() => {
    if ((idField || 0) > 0) {
      selectedExistingFileRef.current = undefined;
      setIsOpeningExistingFile(false);
      setShowAlertDuplicatedFile(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idField]);

  // Left the page before the file finished loading (e.g. load failed and we got
  // redirected to /500). The store is module-level, so it must be cleared here.
  useEffect(() => {
    return () => {
      setIsOpeningExistingFile(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkValidationFileName = () => {
    clearErrors(FileNameNumberFieldName);
    if (isDuplicatedFile &&  isToggleFileAgencyFocus==="fileNameNumber") {
      setError(
        FileNameNumberFieldName,
        {
          type: "isDuplicatedFilename",
          message :" This File Name/Number already exists for this Agency.",
        },
        { shouldFocus: true }
      );
    }
  };

  const onGetFiles = (newInputValue: string): Promise<SCFileResponse[]> => {
    if (!newInputValue) return new Promise<SCFileResponse[]>(() => []);
    return getFiles(newInputValue, agencyField?.id);
  };

  const onValueChanged = (newValue: SCFileResponse | string) => {
    if (typeof newValue !== "string") {
      const { fileId, integrationKey } = newValue;
      // The autocomplete still fires onBlur after an option is picked, which would
      // otherwise run the duplicated-file check and prompt to open the very file
      // the user just chose. Must be set before resetInitialValues() kicks off the
      // form reset that this blur races against.
      setIsOpeningExistingFile(true);
      selectedExistingFileRef.current = newValue.clientFileId;
      resetInitialValues();
      navigate("/file", { state: { fileID: fileId, integrationKey: integrationKey, isExistingFile: true } });
      return;
    }

    setValue(FileNameNumberFieldName, newValue);
    setValue("isFileDefault", false);
    setFileNumber(getValues(FileNameNumberFieldName))
  };

  const handleYes = async (e: any) => {
    setShowAlertDuplicatedFile(false);
    // Same reset + same-route navigate as onValueChanged, so it is exposed to the
    // same blur/reset race.
    setIsOpeningExistingFile(true);
    resetInitialValues();
    fileNameValueRef.current = undefined;
    agencyIdValueRef.current = undefined;
    navigate("/file", { state: { fileID: uniqueFileId, integrationKey: integrationKey } });
  };

  const handleNo = (e: any) => {
    resetNonFileSections(0, getValues(FileNameNumberFieldName), {
      id: "",
      name: "",
    });
    setShowAlertDuplicatedFile(false);
    setIsDuplicatedFile(true);
    setFileNumber(getValues(FileNameNumberFieldName));
  };

  const onBlurHandler = async (e: any) => {
    setToggleFileAgencyFocus("fileNameNumber")
    clearErrors('agency')
    const fileValue = e.target.value;
    if (!fileValue) {
      fileNameValueRef.current = undefined;
      agencyIdValueRef.current = undefined;
      return;
    }

    // A file is already loaded, so this field is disabled and the duplicate check
    // is meaningless. Disabling a focused input fires a real blur, which would
    // otherwise re-check the loaded file against its own agency and prompt the
    // user to open the file that is already open.
    //
    // The second guard covers the file the user just picked from the dropdown
    // while we are still navigating to it. Both read live values (RHF / a ref),
    // so neither can be defeated by a stale closure.
    if (
      ((getValues("id") as number) || 0) > 0 ||
      selectedExistingFileRef.current === fileValue
    ) {
      setFileNumber(getValues(FileNameNumberFieldName));
      return;
    }

    const defaultedAgency = getAgencySelected();
    if (!agencyField || agencyField?.id === "") {
      return;
    }
    
    const prevFileNameValue = fileNameValueRef.current;
    const prevAgencyIdValue = agencyIdValueRef.current;
    // Avoid calls to the API when values are the same between onBlur events
    if (
      prevFileNameValue === fileValue &&
      prevAgencyIdValue === agencyField?.id
    ) {
      return;
    }

    fileNameValueRef.current = fileValue;
    agencyIdValueRef.current = agencyField?.id ?? defaultedAgency;
    
    const fileId = await getUniqueFileByAgencyClientFileId(
      agencyField?.id,
      e.target.value
    );

    // A file was loaded into the form while this check was in flight -> stale.
    if (((getValues("id") as number) || 0) > 0 && !((idField || 0) > 0)) return;

    if (fileId === 0 || !fileId) {
      setIsDuplicatedFile(false);
    }
    if (fileId > 0) {
      setShowAlertDuplicatedFile(true);
    } else if ((idField || 0) > 0)
      resetNonFileSections(0, e.target.value, { id: "", name: "" });

    if(!isAgencyIssueable()){
      clearErrors(FileNameNumberFieldName);
      setError(
        FileNameNumberFieldName,
        {
          type: "manual",
          message: "New files cannot be created for inactive agencies." ,
        },
        { shouldFocus: true }
      );
      // Clear the invalid file name/number so the user must re-enter it.
      setValue(FileNameNumberFieldName, "");
    }
    setFileNumber(getValues(FileNameNumberFieldName))
  };

  const onFocusHandler = (evt: ChangeEvent<any>) => {
    // Focusing the field starts a new interaction, so a previous pick no longer
    // applies (e.g. the navigation it triggered failed and the user came back).
    selectedExistingFileRef.current = undefined;
    if (fileNameNumber) {
      fileNameValueRef.current = fileNameNumber;
    }

    if (agencyField && agencyField?.id) {
      agencyIdValueRef.current = agencyField.id;
    }
  };

  const getOptionLabel = (option: SCFileResponse | string) =>
    isSCFileResponse(option) ? option.clientFileId : option;

  const getOptionSelected = (option: SCFileResponse, value: SCFileResponse) =>
    option.fileId === value.fileId;

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SCFileResponse,
  ) => (
    <Typography
      data-test="fileNameNumber_MenuItem"
      data-fileid={option.fileId}
      component="li" {...props}
    >
      {`${option.clientFileId} - ${option.agencyName}`}
    </Typography>
  );

  const onTypeInputHandler = (inputText: string) => {
    const validValue = inputText.replace(REGEX_NONE_ASCII, "");
    const currentValue: string = getValues(FileNameNumberFieldName);
    setFileNumber(inputText)
    if (currentValue === validValue) {
      setValue(FileNameNumberFieldName, inputText);
    }
    if (inputText !== validValue) {
      setTimeout(() => {
        setValue(FileNameNumberFieldName, validValue);
      }, 10);
    }
  };

  return (
    <>
      <AutocompleteField
        name={FileNameNumberFieldName}
        label="File Name/Number"
        minLengthBeforeSearch={3}
        onGetOptions={onGetFiles}
        onValueChanged={onValueChanged}
        renderOption={renderOption}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        onBlur={onBlurHandler}
        onFocus={(e) => {
          onFocusHandler(e);
        }}
        maxLength={100}
        disabled={Boolean((idField || 0) > 0)}
        forceEnable={Boolean((idField || 0) <= 0)}
        onTypeInput={onTypeInputHandler}
        resetDefaultValue={FileNameNumberFieldName}
        {...rest}
      />
      {showAlertDuplicatedFile && (
        <ConfirmationDialog
          confirmationMessage="The file already exists for your selected agency. Would you like to open the existing file?"
          isOpen={showAlertDuplicatedFile}
          onYes={handleYes}
          onNo={handleNo}
        />
      )}
    </>
  );
};

export default FileNameNumberField;
